/**
 * The gate each stage stands in front of, and what that gate needs.
 *
 * Two questions the UI asks over and over: "which gate is pending here" (the
 * list's gate label, the detail page's one open gate) and "does this stage
 * need the thing this section is about" (team, evidence). Both are answered
 * from the stage code alone, because a stage *is* the position in front of a
 * gate — there is exactly one gate per adjacency.
 *
 * Mirrors `GATE_TRANSITIONS` in the API's `domain/stages.ts`. Static rather
 * than read from the gates endpoint: that endpoint is per-initiative, and a
 * list page would need one request per row to render one label.
 *
 * Operate is absent by design — it is out of the funnel, with nothing ahead.
 */

/** `ref.AiActClass` code whose Gate 1 criterion also demands an evidence row. */
export const AI_ACT_HIGH_RISK = 'HighRisk'

export const FORWARD_GATE: Readonly<Record<string, string>> = {
  Idea: 'G0',
  Shaped: 'G1',
  Experiment: 'G2',
  Pilot: 'G3',
  Scaling: 'G4',
}

/**
 * The gate an initiative at `stageCode` would clear to move forward, or null
 * where there is none. Unknown codes (a stage added to the picklist ahead of
 * a SPA release) return null rather than inventing a gate.
 */
export function forwardGateCode(stageCode: string): string | null {
  return FORWARD_GATE[stageCode] ?? null
}

/**
 * Team is a Gate 1 hard criterion (`g1_team_named` — at least one member),
 * so it is asked for while the initiative is Shaped and nowhere else.
 */
export function stageNeedsTeam(stageCode: string): boolean {
  return forwardGateCode(stageCode) === 'G1'
}

/**
 * Evidence is asked for at two gates: Gate 2 always wants a linked test
 * result (`g2_test_result`), and Gate 1 wants a Compliance review row — but
 * only where the class is high-risk (`g1_ai_act_class`).
 */
export function stageNeedsEvidence(stageCode: string, aiActClassCode: string | null): boolean {
  const gate = forwardGateCode(stageCode)
  if (gate === 'G2') return true
  return gate === 'G1' && aiActClassCode === AI_ACT_HIGH_RISK
}
